import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import printJS from "print-js";

function fmtCurrency(amount) {
  try {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(amount);
  } catch (e) {
    return `R ${amount}`;
  }
}

function inferKeys(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const first = data.find(d => d && typeof d === 'object');
  return first ? Object.keys(first) : [];
}

function buildReportHtml(reportType, data = [], period = '') {
  const keys = inferKeys(data);
  const title = `${reportType} Report ${period ? '— ' + period : ''}`;
  const html = document.createElement('div');
  html.style.position = 'fixed';
  html.style.left = '-10000px';
  html.style.top = '0';
  html.style.width = '1000px';
  html.style.background = '#fff';
  html.style.color = '#111';
  html.style.padding = '24px';

  const headerCols = keys.map(k => `<th style="padding:6px;border:1px solid #eee;text-align:left">${k}</th>`).join('');
  const rows = data.map((row) => {
    const cols = keys.map(k => {
      const v = row[k] == null ? '' : row[k];
      if (k === 'total' || k === 'amount' || k === 'unitPrice') return `<td style="padding:6px;border:1px solid #eee">${fmtCurrency(v)}</td>`;
      return `<td style="padding:6px;border:1px solid #eee">${v}</td>`;
    }).join('');
    return `<tr>${cols}</tr>`;
  }).join('');

  html.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
      <div>
        <div style="font-weight:700;font-size:18px">${title}</div>
      </div>
      <div><small>${new Date().toLocaleString()}</small></div>
    </div>
    <div style="margin-top:12px">
      <table style="width:100%;border-collapse:collapse;margin-top:8px;border:1px solid #eee">
        <thead><tr>${headerCols}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;

  return { html, title };
}

export async function exportReportPdf(reportType, data = [], period = '', fileName) {
  if (!Array.isArray(data) || data.length === 0) return;
  const { html, title } = buildReportHtml(reportType, data, period);
  document.body.appendChild(html);
  const fname = fileName || `${(reportType || 'report').toLowerCase().replace(/\s+/g,'-')}-${period || 'all'}.pdf`;

  try {
    const canvas = await html2canvas(html, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(fname);
  } catch (err) {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write('<html><head><title>' + title + '</title></head><body>' + html.innerHTML + '</body></html>');
      w.document.close();
      w.focus();
      w.print();
      w.close();
    }
  } finally {
    document.body.removeChild(html);
  }
}

export function printReport(reportType, data = [], period = '') {
  if (!Array.isArray(data) || data.length === 0) return;
  const { html, title } = buildReportHtml(reportType, data, period);
  document.body.appendChild(html);
  try {
    printJS({ printable: html.innerHTML, type: 'raw-html', documentTitle: title });
  } catch (e) {
    const w = window.open('', '_blank');
    if (w) {
      w.document.write('<html><head><title>' + title + '</title></head><body>' + html.innerHTML + '</body></html>');
      w.document.close();
      w.focus();
      w.print();
      w.close();
    }
  } finally {
    document.body.removeChild(html);
  }
}

export async function downloadOrderPdf(order) {
  if (!order) return;

  // Build minimal HTML fragment for the order
  const html = document.createElement('div');
  html.style.position = 'fixed';
  html.style.left = '-10000px';
  html.style.top = '0';
  html.style.width = '800px';
  html.style.background = '#fff';
  html.style.color = '#111';
  html.style.padding = '24px';
  html.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
      <div>
        <div style="font-weight:700;font-size:18px">Order ${order.id}</div>
        <div style="color:#666">${order.date || ''}</div>
      </div>
      <div><strong>${order.status || ''}</strong></div>
    </div>

    <div style="margin-top:12px">
      <div style="display:flex;gap:8px;margin-bottom:6px"><div style="width:120px;color:#666">Customer</div><div>${order.customer || ''} <span style="color:#666">${order.customerId || ''}</span></div></div>
      <div style="display:flex;gap:8px;margin-bottom:6px"><div style="width:120px;color:#666">Region</div><div>${order.region || ''}</div></div>
    </div>

    <div style="margin-top:12px">
      <table style="width:100%;border-collapse:collapse;margin-top:8px;border:1px solid #eee">
        <thead><tr><th style="padding:8px;border:1px solid #eee;text-align:left">Product</th><th style="padding:8px;border:1px solid #eee;text-align:left">Quantity</th><th style="padding:8px;border:1px solid #eee;text-align:left">Unit</th><th style="padding:8px;border:1px solid #eee;text-align:left">Unit Price</th><th style="padding:8px;border:1px solid #eee;text-align:left">Total</th></tr></thead>
        <tbody>
          <tr>
            <td style="padding:8px;border:1px solid #eee">${order.product || ''}</td>
            <td style="padding:8px;border:1px solid #eee">${order.qty != null ? order.qty : ''}</td>
            <td style="padding:8px;border:1px solid #eee">${order.unit || ''}</td>
            <td style="padding:8px;border:1px solid #eee">${order.unitPrice != null ? fmtCurrency(order.unitPrice) : ''}</td>
            <td style="padding:8px;border:1px solid #eee">${order.total != null ? fmtCurrency(order.total) : ''}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div style="margin-top:12px">
      <div style="display:flex;gap:8px;margin-bottom:6px"><div style="width:120px;color:#666">Delivery Date</div><div>${order.delivery || ''}</div></div>
      <div style="display:flex;gap:8px;margin-bottom:6px"><div style="width:120px;color:#666">Driver</div><div>${order.driver || '—'}</div></div>
    </div>

    <div style="margin-top:12px"><div style="color:#666">Notes</div><div>${order.notes || '—'}</div></div>
  `;

  document.body.appendChild(html);

  try {
    const canvas = await html2canvas(html, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const imgProps = pdf.getImageProperties(imgData);
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`Order-${order.id}.pdf`);
  } catch (err) {
    // final fallback: open print dialog of the fragment
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write('<html><head><title>Order</title></head><body>' + html.innerHTML + '</body></html>');
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  } finally {
    document.body.removeChild(html);
  }
}
